import { ChannelName, Service } from './service';
import { ITokenManager, ITokenStorage, SSOTokens } from '../token-manager/token-manager.types';
import { CHANNEL_NAMES, LoggerNames } from '../lib/constants';
import { BroadcastChannel } from 'broadcast-channel';
import { isBrowser } from '../lib/browser';
import { Logger } from '../lib/logger';
import { AuthSdkError } from '../lib/auth-sdk-error';
import { exhaustiveMatch } from '../lib/assert';

export interface SyncStorageServiceOptions {
  syncStorage?: boolean;
  syncChannelName?: ChannelName;
}

export type SyncStorageMessage =
  | {
      type: 'added' | 'renewed';
      tokens: SSOTokens;
    }
  | { type: 'removed'; tokens: SSOTokens | null }
  | { type: 'set-storage'; payload: ITokenStorage };

export class SyncStorageService implements Service {
  private readonly logger = Logger.createLogger(LoggerNames.SYNC_STORAGE);
  private readonly options: Required<SyncStorageServiceOptions>;
  private readonly tokenManager: ITokenManager;

  private started: boolean = false;
  private channel?: BroadcastChannel<SyncStorageMessage>;
  private enablePostMessage: boolean = true;

  constructor(tokenManager: ITokenManager, options: SyncStorageServiceOptions) {
    this.options = {
      syncStorage: options.syncStorage ?? true,
      syncChannelName: options.syncChannelName ?? CHANNEL_NAMES.SYNC_STORAGE,
    };
    this.tokenManager = tokenManager;

    this.onTokenAdded = this.onTokenAdded.bind(this);
    this.onTokenRenewed = this.onTokenRenewed.bind(this);
    this.onTokenRemoved = this.onTokenRemoved.bind(this);
    this.onSetStorage = this.onSetStorage.bind(this);
    this.onMessage = this.onMessage.bind(this);
  }

  canStart(): Boolean {
    return !this.started && Boolean(this.options.syncStorage) && isBrowser();
  }

  isStarted(): Boolean {
    return this.started;
  }

  requiresLeadership(): boolean {
    return false;
  }

  async start(): Promise<void> {
    if (!this.canStart()) {
      this.logger.debug('Service cannot start. Aborting');
      return;
    }
    const { syncChannelName } = this.options;
    try {
      this.channel = new BroadcastChannel<SyncStorageMessage>(syncChannelName);
    } catch (err) {
      this.logger.debug(err);
      throw new AuthSdkError('Failed to create channel. SyncStorage is not supported in this browser');
    }

    this.tokenManager.on('added', this.onTokenAdded);
    this.tokenManager.on('renewed', this.onTokenRenewed);
    this.tokenManager.on('removed', this.onTokenRemoved);
    this.tokenManager.on('set-storage', this.onSetStorage);
    this.channel.addEventListener('message', this.onMessage);
    this.started = true;
  }

  async stop(): Promise<void> {
    if (!this.isStarted()) {
      this.logger.debug('Service already stopped. Aborting');
      return;
    }
    this.tokenManager.off('added', this.onTokenAdded);
    this.tokenManager.off('renewed', this.onTokenRenewed);
    this.tokenManager.off('removed', this.onTokenRemoved);
    this.tokenManager.off('set-storage', this.onSetStorage);
    this.channel?.removeEventListener('message', this.onMessage);
    await this.channel?.close();
    this.channel = undefined;
    this.started = false;
  }

  private onTokenAdded(payload: SSOTokens) {
    if (!this.enablePostMessage) {
      return;
    }

    return this.channel?.postMessage({
      type: 'added',
      tokens: payload,
    });
  }

  private onTokenRemoved(payload: SSOTokens | null) {
    if (!this.enablePostMessage) {
      return;
    }

    return this.channel?.postMessage({
      type: 'removed',
      tokens: payload,
    });
  }

  private onTokenRenewed(payload: SSOTokens) {
    if (!this.enablePostMessage) {
      return;
    }

    return this.channel?.postMessage({
      type: 'renewed',
      tokens: payload,
    });
  }

  private onSetStorage(storage: ITokenStorage) {
    if (!this.enablePostMessage) {
      return;
    }

    return this.channel?.postMessage({
      type: 'set-storage',
      payload: storage,
    });
  }

  private onMessage(message: SyncStorageMessage) {
    // This is to prevent a loop of messages between tabs
    this.enablePostMessage = false;
    const type = message.type;
    switch (type) {
      case 'added':
        this.logger.debug('Received added message', message.tokens);
        this.tokenManager.emit('added', message.tokens);
        break;
      case 'removed':
        this.logger.debug('Received removed message', message.tokens);
        this.tokenManager.emit('removed', message.tokens);
        break;
      case 'renewed':
        this.logger.debug('Received renewed message', message.tokens);
        this.tokenManager.emit('renewed', message.tokens);
        break;
      case 'set-storage':
        this.logger.debug('Received set-tokens message', message);
        this.tokenManager.setStorage(message.payload);
        break;
      default:
        exhaustiveMatch(type, new AuthSdkError(`Unknown sync message type ${type}`));
    }
    this.enablePostMessage = true;
  }
}
